import React, { useMemo, useState } from 'react';

import { LOAD_MORE_PAGE_SIZE } from '~constants/common';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useSearch } from '~libs/searchParams';

import { Button } from 'reactstrap';

import { ClickableIcon, ErrorResponse, Toolbar } from '~components';
import { SearchBar } from '~components/form-fields/search';
import Icon, { faMagnifyingGlass, faPlus } from '~components/icon';
import { TableView } from '~components/table-view-v3';

import BatchStatsCards from '~direct-debits/components/payment-batches/BatchStatsCards';
import { columnsFactory, PaymentBatchRow } from '~direct-debits/forms/payment-batches/list';
import { searchFields } from '~direct-debits/forms/payment-batches/search';

import { useGetPaymentBatchesQuery } from '~direct-debits/actions/paymentBatches';
import { useGetConnectionsQuery } from '~direct-debits/actions/connections';

interface ComponentProps {
  isActive?: boolean;
  onSelectBatch: (batchId: string) => void;
  onCreate?: () => void;
}

const PaymentBatchesList: React.FC<ComponentProps> = ({ isActive = true, onSelectBatch, onCreate }) => {
  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [searchParams, setParams] = useSearch('dd-payment-batches-search', { limit: LOAD_MORE_PAGE_SIZE });

  const handleSearchSubmit = (values: Record<string, any>) => setParams({ ...values, limit: LOAD_MORE_PAGE_SIZE });

  const { data, isFetching, isError, error } = useGetPaymentBatchesQuery(searchParams, { skip: !isActive });
  const { data: connections } = useGetConnectionsQuery();
  const hasActiveConnection = (connections ?? []).some((c) => c.status === 'active');

  // TableView rows must carry an `id` field — payment batch IDs are already
  // unique strings, but expose them on the standard key for clarity.
  const rows = useMemo<PaymentBatchRow[]>(
    () => (data?.rows ?? []).map((row) => ({ ...row }) as PaymentBatchRow),
    [data?.rows],
  );
  const total = data?.total ?? 0;
  const stats = data?.stats;

  const handleLoadMore = () => {
    const currentLimit = Number(searchParams.limit) || LOAD_MORE_PAGE_SIZE;
    setParams({ ...searchParams, limit: currentLimit + LOAD_MORE_PAGE_SIZE });
  };

  const columns = useMemo(() => columnsFactory({ onSelectBatch }), [onSelectBatch]);

  return (
    <div className="p-2 dd-payment-batches" data-cy="dd-payment-batches-list">
      <BatchStatsCards stats={stats} isLoading={isFetching && !data} />
      <Toolbar
        id="dd-payment-batches"
        rightCluster={
          <>
            {onCreate && (
              <Button
                color="success"
                size="sm"
                className="me-2"
                onClick={onCreate}
                data-cy="dd-payment-batches-create-button"
                disabled={!hasActiveConnection}
                title={hasActiveConnection ? undefined : 'GoCardless connection is not active'}
              >
                <Icon icon={faPlus} className="me-2" />
                Create Batch
              </Button>
            )}
            <ClickableIcon
              className="me-2"
              icon={faMagnifyingGlass}
              id="dd-payment-batches-search-button"
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
              ariaLabel="Toggle search bar"
            />
          </>
        }
      />
      <SearchBar
        isOpen={searchBarOpen}
        fields={searchFields}
        initialValues={searchParams}
        onSubmit={handleSearchSubmit}
      />
      {isError ? (
        <ErrorResponse message={getErrorMessage(error)} />
      ) : (
        <TableView
          id="dd-payment-batches-table"
          columns={columns}
          data={rows}
          isLoading={isFetching}
          emptyMessage="No payment batches found"
          readonly
          striped
          allowPagination
          totalCount={total}
          onLoadMore={handleLoadMore}
          showCount
          pageSize={Number(searchParams.limit) || LOAD_MORE_PAGE_SIZE}
        />
      )}
    </div>
  );
};

export default PaymentBatchesList;
